import type { ChangeEvent, DragEvent, ReactElement } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Camera, GripVertical, ImagePlus, Trash2 } from 'lucide-react';
import { api, SpaceSummary } from '../api';
import { MarkdownEditor } from '../components/MarkdownEditor';
import { StatusBadge } from '../components/StatusBadge';
import { InviteCodeDisplay } from '../components/InviteCodeDisplay';

const DEFAULT_FORM = {
  title: '',
  description: '',
  ueClientDownloadUrl: '',
  publicType: 0,
  featuredImages: [] as string[],
};

export function SpaceEditor(): ReactElement {
  const params = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isNew = !params.id;

  const [form, setForm] = useState(DEFAULT_FORM);
  const [space, setSpace] = useState<SpaceSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [screenshot, setScreenshot] = useState<{
    url: string;
    width?: number;
    height?: number;
  } | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!params.id) return;
    const id = params.id;
    let cancelled = false;
    void (async () => {
      try {
        const s = await api.getSpace(id);
        if (!cancelled) {
          setSpace(s);
          setForm({
            title: s.title,
            description: s.description ?? '',
            ueClientDownloadUrl: s.ueClientDownloadUrl ?? '',
            publicType: s.publicType ?? 0,
            featuredImages: s.featuredImages ?? [],
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'failed to load');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [params.id]);

  const handleUpload = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await api.uploadImage(file, 'space_image');
      setForm((prev) => {
        const next = [...prev.featuredImages, result.url];
        void persistOrder(next);
        return { ...prev, featuredImages: next };
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCaptureScreenshot = async (): Promise<void> => {
    if (!params.id) {
      setError('Save the space first before capturing a screenshot.');
      return;
    }
    setCapturing(true);
    setError(null);
    try {
      const result = await api.captureScreenshot(params.id);
      setScreenshot(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'screenshot capture failed');
    } finally {
      setCapturing(false);
    }
  };

  const addToFeatured = (): void => {
    if (!screenshot) return;
    setForm((prev) => {
      const next = [...prev.featuredImages, screenshot.url];
      void persistOrder(next);
      return { ...prev, featuredImages: next };
    });
    setScreenshot(null);
  };

  const removeImage = (url: string): void => {
    setForm((prev) => {
      const next = prev.featuredImages.filter((u) => u !== url);
      void persistOrder(next);
      return { ...prev, featuredImages: next };
    });
  };

  const persistOrder = useCallback(
    async (images: string[]): Promise<void> => {
      if (!params.id) return; // new space — order saved on Create
      try {
        await api.updateSpace(params.id, { featured_images: images });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'reorder save failed');
      }
    },
    [params.id],
  );

  const reorderImages = useCallback(
    (from: number, to: number): void => {
      if (from === to) return;
      setForm((prev) => {
        const next = [...prev.featuredImages];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved!);
        void persistOrder(next);
        return { ...prev, featuredImages: next };
      });
    },
    [persistOrder],
  );

  const handleDragStart = (index: number): void => {
    setDragIndex(index);
  };

  const handleDragOver = (e: DragEvent, index: number): void => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropIndex(index);
  };

  const handleDragLeave = (): void => {
    setDropIndex(null);
  };

  const handleDrop = (e: DragEvent, index: number): void => {
    e.preventDefault();
    if (dragIndex !== null) {
      reorderImages(dragIndex, index);
    }
    setDragIndex(null);
    setDropIndex(null);
  };

  const handleDragEnd = (): void => {
    setDragIndex(null);
    setDropIndex(null);
  };

  const save = async (): Promise<void> => {
    setError(null);
    setSaving(true);
    try {
      if (isNew) {
        const created = await api.createSpace({
          title: form.title,
          description: form.description || undefined,
          featured_images: form.featuredImages,
          ue_client_download_url: form.ueClientDownloadUrl || undefined,
          public_type: form.publicType,
        });
        navigate(`/spaces/${created.id}`);
        return;
      }
      if (!params.id) return;
      const id = params.id;
      const updated = await api.updateSpace(id, {
        title: form.title,
        description: form.description || null,
        featured_images: form.featuredImages,
        ue_client_download_url: form.ueClientDownloadUrl || null,
        public_type: form.publicType,
      });
      setSpace(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'save failed');
    } finally {
      setSaving(false);
    }
  };

  const publish = async (): Promise<void> => {
    if (!params.id) return;
    await api.publishSpace(params.id);
    const updated = await api.getSpace(params.id);
    setSpace(updated);
  };

  const archive = async (): Promise<void> => {
    if (!params.id) return;
    await api.archiveSpace(params.id);
    const updated = await api.getSpace(params.id);
    setSpace(updated);
  };

  return (
    <section className="space-y-4 max-w-2xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/spaces')}
            aria-label="Back to spaces"
            className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-200"
          >
            <ArrowLeft size={16} aria-hidden="true" />
            Back
          </button>
          <h1 className="text-lg font-semibold">
            {isNew ? 'New Space' : `Edit Space`}
          </h1>
        </div>
        {space && <StatusBadge status={space.status} />}
      </div>
      {error && <p className="text-rose-400 text-sm">{error}</p>}
      <label className="block text-xs text-slate-400">Title</label>
      <input
        type="text"
        value={form.title}
        onChange={(e) => setForm({ ...form, title: e.target.value })}
        className="w-full bg-slate-900 border border-slate-700 rounded p-2"
      />
      <label className="block text-xs text-slate-400 mt-3">Description</label>
      <MarkdownEditor
        value={form.description}
        onChange={(next) => setForm({ ...form, description: next })}
      />
      <label className="block text-xs text-slate-400 mt-3">
        UE Client Download URL
      </label>
      <input
        type="url"
        value={form.ueClientDownloadUrl}
        onChange={(e) =>
          setForm({ ...form, ueClientDownloadUrl: e.target.value })
        }
        className="w-full bg-slate-900 border border-slate-700 rounded p-2"
      />
      <label className="block text-xs text-slate-400 mt-3">Visibility</label>
      <div className="flex gap-4">
        {[
          [0, 'Private'],
          [1, 'Allow List'],
          [2, 'Public'],
        ].map(([value, label]) => (
          <label
            key={value}
            className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer"
          >
            <input
              type="radio"
              name="publicType"
              value={value}
              checked={form.publicType === value}
              onChange={() => setForm({ ...form, publicType: value as number })}
              className="accent-accent"
            />
            {label}
          </label>
        ))}
      </div>
      <label className="block text-xs text-slate-400 mt-3">
        Featured Images
      </label>
      <div className="flex flex-wrap gap-3 items-end">
        {form.featuredImages.map((url, i) => (
          <div
            key={url}
            draggable
            onDragStart={() => handleDragStart(i)}
            onDragOver={(e) => handleDragOver(e, i)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, i)}
            onDragEnd={handleDragEnd}
            className={`relative group w-40 aspect-[1.618/1] rounded-md border overflow-hidden bg-slate-900 cursor-grab active:cursor-grabbing transition-all ${
              dragIndex === i ? 'opacity-40 scale-95' : ''
            } ${
              dropIndex === i && dragIndex !== i
                ? 'border-accent border-2 -translate-y-1 shadow-lg shadow-accent/20'
                : 'border-slate-700'
            }`}
          >
            <div className="absolute top-0.5 left-0.5 p-0.5 rounded bg-slate-900/80 text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity">
              <GripVertical size={14} />
            </div>
            <img
              src={url}
              alt=""
              className="w-full h-full object-cover pointer-events-none"
              draggable={false}
            />
            <button
              type="button"
              onClick={() => removeImage(url)}
              title="Remove image"
              className="absolute top-0.5 right-0.5 p-0.5 rounded bg-slate-900/80 text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-40 aspect-[1.618/1] rounded-md border border-dashed border-slate-600 flex flex-col items-center justify-center gap-1 text-slate-500 hover:border-slate-400 hover:text-slate-300 disabled:opacity-50 transition-colors"
        >
          <ImagePlus size={20} />
          <span className="text-[10px]">{uploading ? '...' : 'Upload'}</span>
        </button>
        {params.id && (
          <button
            type="button"
            onClick={() => void handleCaptureScreenshot()}
            disabled={capturing}
            className="w-40 aspect-[1.618/1] rounded-md border border-dashed border-accent/50 flex flex-col items-center justify-center gap-1 text-accent/70 hover:border-accent hover:text-accent disabled:opacity-50 transition-colors"
          >
            <Camera size={20} />
            <span className="text-[10px]">{capturing ? '...' : 'Screenshot'}</span>
          </button>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        onChange={(e) => { void handleUpload(e); }}
        className="hidden"
      />
      {screenshot && (
        <div className="mt-3 p-3 rounded-md border border-accent/40 bg-accent/5">
          <p className="text-xs text-slate-400 mb-2">
            Screenshot captured
            {screenshot.width && screenshot.height
              ? ` (${screenshot.width}×${screenshot.height})`
              : ''}
          </p>
          <div className="flex items-start gap-3">
            <div className="w-60 aspect-[1.618/1] rounded-md border border-slate-700 overflow-hidden bg-slate-900 shrink-0">
              <img
                src={screenshot.url}
                alt="Viewport screenshot"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={addToFeatured}
                className="px-3 py-1.5 rounded bg-accent text-slate-900 text-sm font-medium hover:bg-accent/90 transition-colors"
              >
                Add to Featured Images
              </button>
              <button
                type="button"
                onClick={() => setScreenshot(null)}
                className="px-3 py-1.5 rounded bg-slate-700 text-slate-300 text-sm hover:bg-slate-600 transition-colors"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}
      {!isNew && space && (
          <section className="border-t border-slate-700 pt-3 mt-3">
            <InviteCodeDisplay
              spaceId={space.id}
              inviteCode={space.inviteCode}
              inviteCodeGeneratedAt={space.inviteCodeGeneratedAt}
              inviteCodeUsedAt={space.inviteCodeUsedAt}
              onRegenerated={(newCode) => {
                setSpace({ ...space, inviteCode: newCode });
              }}
            />
          </section>
      )}

      <div className="flex gap-2 pt-3">
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving || form.title.length === 0}
          className="bg-accent text-slate-900 rounded px-4 py-1.5 font-medium disabled:opacity-50"
        >
          {isNew ? 'Create' : 'Save'}
        </button>
        {!isNew && (space?.status === 0 || space?.status === 4) && (
          <button
            type="button"
            onClick={() => void publish()}
            className="bg-emerald-700 text-emerald-100 rounded px-3 py-1.5 text-sm"
          >
            Publish
          </button>
        )}
        {!isNew && (
          <button
            type="button"
            onClick={() => void archive()}
            className="bg-rose-700 text-rose-100 rounded px-3 py-1.5 text-sm"
          >
            Archive
          </button>
        )}
      </div>
    </section>
  );
}
